import { Box, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { Order, Unit } from '../../../apiTypes';
import PrintModal from '../../../components/PrintModal';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import { updateDoc } from '../../../utils/api';

interface Props {
  onClose: () => void;
  order: DbDocument<Order>;
  kitchenId?: string;
  autoPrint?: boolean;
}

type Item = {
  id: string;
  name: string;
  qnt: number;
  weight: string[];
  unit: Unit;
  variation?: string | null;
};

const KitchenTicketPrintModal = (props: Props) => {
  const { onClose, order, autoPrint, kitchenId } = props;
  const { cafe, cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const { dishesMap, kitchensMap } = useCafe();
  const fontWeight = cafe?.bill?.boldText ? 800 : 400;
  const kitchenName = kitchenId && kitchensMap[kitchenId]?.name;

  const itemsMap = useMemo(
    () =>
      Object.values(order.items)
        .filter((i) => !i.printed && dishesMap[i.dishId]?.kitchenId === kitchenId)
        .reduce<Record<string, Item>>((acc, orderItem) => {
          const key = `${orderItem.dishId}-${orderItem.variation?.name}`;
          return {
            ...acc,
            [key]: {
              id: orderItem.dishId,
              name: orderItem.name || dishesMap[key]?.name || '',
              qnt: (acc[key]?.qnt || 0) + 1,
              weight: orderItem.byWeight
                ? [...(acc[key]?.weight || []), `${orderItem.weight}${t[orderItem.unit || 'g'][lang]}`]
                : [],
              unit: orderItem.unit || 'g',
              variation: orderItem.variation?.name,
            },
          };
        }, {}),
    [dishesMap, kitchenId, lang, order.items]
  );

  const handlePrint = useCallback(async () => {
    const updatedItemsId = Object.values(order.items).filter(
      (i) => !i.printed && dishesMap[i.dishId]?.kitchenId === kitchenId
    );

    const update = updatedItemsId.reduce(
      (acc, item) => ({
        ...acc,
        [`items.${item.id}.printed`]: true,
      }),
      {}
    );

    updateDoc<Order>(`cafes/${cafeSlug}/orders/${order?.id}`, update);

    onClose();
  }, [cafeSlug, dishesMap, kitchenId, onClose, order?.id, order.items]);

  return (
    <PrintModal onClose={onClose} onPrint={handlePrint} isPosPrinter autoPrint={autoPrint}>
      <Box p={2} pb={5} color="#000000">
        {kitchenName && (
          <Typography sx={{ textAlign: 'center', fontWeight, fontSize: 20, mb: 1 }}>
            {kitchenName?.toUpperCase()}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ textAlign: 'center', fontWeight }}>{`${t.order[lang]} #${order.number}`}</Typography>
          <Typography sx={{ textAlign: 'center', fontWeight }}>{format(order.created, 'dd.MM.yyyy HH:mm')}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight }}>{`${t.waiter[lang]}: ${order.workerName}`}</Typography>
          {order.tableName && <Typography sx={{ fontWeight }}>{`${t.table[lang]}: ${order.tableName}`}</Typography>}
        </Box>
        {order.comment && <Typography sx={{ fontWeight }}>{order.comment}</Typography>}

        <Grid container spacing={0.5} sx={{ borderBottom: '1px solid #000', mb: 1, mt: 2 }}>
          <Grid item xs={8}>
            <Typography variant="body2" sx={{ fontWeight }}>
              {t.name[lang]}
            </Typography>
          </Grid>
          <Grid item xs={4} textAlign="right">
            <Typography variant="body2" sx={{ fontWeight, whiteSpace: 'nowrap' }}>
              {t.qty[lang]}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ borderBottom: '1px solid #000' }}>
          {Object.values(itemsMap).map((i) => (
            <Grid key={i.id} container spacing={0.5} sx={{ lineHeight: 1.1, mb: 1 }}>
              <Grid item xs={8}>
                <Typography sx={{ lineHeight: 1.2, fontWeight, fontSize: 22 }}>
                  {i.name}
                  {i.variation && (
                    <Typography component="span" sx={{ lineHeight: 1.2, fontWeight, fontSize: 16 }}>
                      {` (${i.variation})`}
                    </Typography>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={4} textAlign="right">
                {i.weight.length === 0 && (
                  <Typography sx={{ lineHeight: 1.2, fontWeight, fontSize: 22 }}>{i.qnt}</Typography>
                )}
                {i.weight.length > 0 &&
                  i.weight.map((weight, i) => (
                    <Typography key={i} sx={{ lineHeight: 1.2, fontWeight, fontSize: 20 }}>
                      {weight}
                    </Typography>
                  ))}
              </Grid>
            </Grid>
          ))}
        </Box>
      </Box>
    </PrintModal>
  );
};

export default KitchenTicketPrintModal;
